/**
 * Orchestrates element placement during drag operations using the declarative
 * computeElementPlacements service.
 *
 * This module bridges between the TimelineElement component and the pure
 * computation service, handling:
 * - Reading original positions from EditorStore
 * - Computing placements for main video track elements
 * - Applying results to TimelineStore overrides
 *
 * @module services/timeline/editing/applyDeclarativePlacements
 */

import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import {
  type ElementBoundary,
  SNAP_WARNING_MULTIPLIER,
} from '@src/utility/elementSnapping';

import {
  computeElementPlacements,
  doesPositionTriggerSwap,
  type ElementPosition,
  type PlacementResult,
  type TrackElement,
} from './computeElementPlacements';

import type { Element } from '@src/editor/models/Element';
import type { ElementState } from '@src/renderer/ElementState';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type TimelineStore from '@src/stores/TimelineStore';

/**
 * Regenerates snap boundaries for main track elements based on their displaced positions.
 *
 * After swaps occur, the main track elements have new positions that differ from the
 * snapshot boundaries captured at drag start. This function creates fresh boundaries
 * from the computed positions so edge snapping uses up-to-date positions.
 *
 * @param result - The placement result from computeElementPlacements
 * @param draggedElementId - ID of the element being dragged (excluded from boundaries)
 * @param elements - Original track elements (for duration lookup)
 * @returns New boundaries for main track elements at their displaced positions
 */
export function regenerateMainTrackBoundaries(
  result: PlacementResult,
  draggedElementId: string,
  elements: ReadonlyArray<TrackElement>,
): ElementBoundary[] {
  const boundaries: ElementBoundary[] = [];

  // Build duration lookup map
  const durationMap = new Map<string, number>();
  for (const el of elements) {
    durationMap.set(el.id, el.duration);
  }

  for (const [elementId, position] of result.positions) {
    // Skip the dragged element - we don't snap to ourselves
    if (elementId === draggedElementId) {
      continue;
    }

    const duration = durationMap.get(elementId);
    if (duration === undefined) {
      continue; // Should not happen, but be defensive
    }

    // Add head (start) boundary
    boundaries.push({
      time: position.globalTime,
      type: 'start',
      elementId,
    });

    // Add tail (end) boundary
    boundaries.push({
      time: position.globalTime + duration,
      type: 'end',
      elementId,
    });
  }

  // Sort by time for efficient lookup
  boundaries.sort((a, b) => a.time - b.time);

  return boundaries;
}

/**
 * Merges regenerated main-track boundaries with other-track snapshot boundaries.
 *
 * After computing swaps, main track elements have moved but other tracks haven't.
 * This function:
 * 1. Takes regenerated main-track boundaries (current positions post-swap)
 * 2. Filters snapshot boundaries to only those from OTHER tracks
 * 3. Merges them into a single sorted array for snapping
 *
 * @param regeneratedMainTrack - Boundaries from main track at displaced positions
 * @param snapshotBoundaries - Original snapshot boundaries (all tracks)
 * @param mainTrackElementIds - Set of element IDs on the main track (to filter them out of snapshot)
 * @returns Combined, sorted boundaries for edge snapping
 */
export function mergeTrackBoundaries(
  regeneratedMainTrack: ReadonlyArray<ElementBoundary>,
  snapshotBoundaries: ReadonlyArray<ElementBoundary>,
  mainTrackElementIds: ReadonlySet<string>,
): ElementBoundary[] {
  // Filter snapshot to only other-track boundaries (reference + non-main-track elements)
  const otherTrackBoundaries = snapshotBoundaries.filter(boundary => {
    // Keep reference boundaries (playhead, markers)
    if (boundary.type === 'reference') {
      return true;
    }
    // Filter out main track elements (they're replaced by regenerated boundaries)
    return !mainTrackElementIds.has(boundary.elementId);
  });

  // Combine and sort
  const merged = [...regeneratedMainTrack, ...otherTrackBoundaries];
  merged.sort((a, b) => a.time - b.time);

  return merged;
}

/**
 * Result of edge snapping computation
 */
export interface EdgeSnappingResult {
  /** The snapped global time (or original if no snap) */
  snappedTime: number;
  /** Whether snapping was applied */
  didSnap: boolean;
  /** The boundary that was snapped to (if any) */
  snappedBoundary?: ElementBoundary;
  /** Whether the snap was to the head edge (true) or tail edge (false) */
  isHeadSnap?: boolean;
  /** Whether the element is within warning range (5x snap threshold) of a boundary */
  isWithinWarningRange: boolean;
  /** The closest boundary within warning range (for displaying snap guide) */
  warningBoundary?: ElementBoundary;
}

/**
 * Applies edge snapping by checking HEAD and/or TAIL edges.
 *
 * By default, both edges are checked regardless of drag direction. This allows
 * the user to snap, overshoot, and return to the same boundary.
 *
 * When in overlap mode, only the leading edge should be checked (controlled via checkEdge).
 *
 * @param proposedTime - The proposed globalTime for the dragged element
 * @param duration - Duration of the dragged element (for tail calculation)
 * @param boundaries - All snap boundaries (merged from all tracks)
 * @param snapThreshold - Distance threshold for snapping (in seconds)
 * @param currentSnap - Currently active snap (for stickiness), if any
 * @param checkEdge - Which edge(s) to check: 'both' (default), 'head', or 'tail'
 * @returns Snapping result with adjusted time and snap state
 */
export function applyEdgeSnapping(
  proposedTime: number,
  duration: number,
  boundaries: ReadonlyArray<ElementBoundary>,
  snapThreshold: number,
  currentSnap?: ElementBoundary,
  checkEdge: 'both' | 'head' | 'tail' = 'both',
): EdgeSnappingResult {
  const headEdge = proposedTime;
  const tailEdge = proposedTime + duration;
  const warningThreshold = snapThreshold * SNAP_WARNING_MULTIPLIER;

  let bestSnap: {
    boundary: ElementBoundary;
    distance: number;
    isHeadSnap: boolean;
  } | null = null;

  // Also track the closest boundary within warning range (for guide display)
  let closestWarning: {
    boundary: ElementBoundary;
    distance: number;
  } | null = null;

  // Check stickiness first - if currently snapped and still within threshold, stay snapped
  if (currentSnap) {
    const currentBoundaryStillValid = boundaries.some(
      b => b.elementId === currentSnap.elementId && b.type === currentSnap.type,
    );

    if (currentBoundaryStillValid) {
      const distFromHead = Math.abs(headEdge - currentSnap.time);
      const distFromTail = Math.abs(tailEdge - currentSnap.time);

      // Determine which edge checks are valid based on checkEdge option.
      // A tail snap places the head at currentSnap.time - duration; reject it
      // when that would push the head before the timeline start (< 0).
      const headValid = checkEdge !== 'tail' && distFromHead <= snapThreshold;
      const tailValid =
        checkEdge !== 'head' &&
        distFromTail <= snapThreshold &&
        currentSnap.time - duration >= 0;

      // Check if we're still within threshold of the current snap
      if (headValid || tailValid) {
        // Maintain the snap - use whichever valid edge is closer
        const isHeadSnap =
          headValid && (!tailValid || distFromHead <= distFromTail);
        const snappedTime = isHeadSnap
          ? currentSnap.time // snap head to boundary
          : currentSnap.time - duration; // snap tail to boundary
        return {
          snappedTime,
          didSnap: true,
          snappedBoundary: currentSnap,
          isHeadSnap,
          isWithinWarningRange: true,
          warningBoundary: currentSnap,
        };
      }
    }
    // Otherwise, the snap is broken - look for a new one
  }

  // Look for closest boundary within threshold
  // Check edges based on checkEdge option; also track warning range candidates
  const shouldCheckHead = checkEdge !== 'tail';
  const shouldCheckTail = checkEdge !== 'head';

  for (const boundary of boundaries) {
    // Check head edge (if enabled)
    if (shouldCheckHead) {
      const headDistance = Math.abs(headEdge - boundary.time);
      if (headDistance <= snapThreshold) {
        if (!bestSnap || headDistance < bestSnap.distance) {
          bestSnap = { boundary, distance: headDistance, isHeadSnap: true };
        }
      }
      // Track warning range for head
      if (headDistance <= warningThreshold) {
        if (!closestWarning || headDistance < closestWarning.distance) {
          closestWarning = { boundary, distance: headDistance };
        }
      }
    }

    // Check tail edge (if enabled). A tail snap places the head at
    // boundary.time - duration; skip boundaries that would push the head
    // before the timeline start (< 0), since that position is impossible.
    if (shouldCheckTail && boundary.time - duration >= 0) {
      const tailDistance = Math.abs(tailEdge - boundary.time);
      if (tailDistance <= snapThreshold) {
        if (!bestSnap || tailDistance < bestSnap.distance) {
          bestSnap = { boundary, distance: tailDistance, isHeadSnap: false };
        }
      }
      // Track warning range for tail
      if (tailDistance <= warningThreshold) {
        if (!closestWarning || tailDistance < closestWarning.distance) {
          closestWarning = { boundary, distance: tailDistance };
        }
      }
    }
  }

  if (bestSnap) {
    const snappedTime = bestSnap.isHeadSnap
      ? bestSnap.boundary.time // Head aligns with boundary
      : bestSnap.boundary.time - duration; // Tail aligns with boundary

    return {
      snappedTime,
      didSnap: true,
      snappedBoundary: bestSnap.boundary,
      isHeadSnap: bestSnap.isHeadSnap,
      isWithinWarningRange: true,
      warningBoundary: bestSnap.boundary,
    };
  }

  // No snap found - but may still be within warning range
  return {
    snappedTime: proposedTime,
    didSnap: false,
    isWithinWarningRange: closestWarning !== null,
    warningBoundary: closestWarning?.boundary,
  };
}

/** Track number at or above which elements are considered "hidden" (karaoke, internal state) */
const HIDDEN_TRACK_THRESHOLD = KARAOKE_TRACK_NUMBER;

/**
 * Information about an element that the dragged element overlaps with
 */
export interface OverlapInfo {
  /** The overlapped element's ID */
  elementId: string;
  /** Overlapped element's current head position */
  head: number;
  /** Overlapped element's current tail position */
  tail: number;
  /** Overlapped element's midpoint */
  midpoint: number;
}

/**
 * Result of overlap resolution calculation
 */
export interface OverlapResolutionResult {
  /** The boundary time the dragged element should snap to */
  snapBoundaryTime: number;
  /** Whether this resolution triggers a swap */
  triggersSwap: boolean;
  /** The resolved head position for the dragged element */
  resolvedHead: number;
}

/**
 * Finds an element that overlaps with the dragged element.
 * Uses displaced positions from the first-pass computation.
 *
 * @param draggedId - ID of the element being dragged
 * @param draggedHead - Current head position of dragged element
 * @param draggedTail - Current tail position of dragged element
 * @param positions - Map of element positions after first-pass computation
 * @param durations - Map of element IDs to durations
 * @returns The overlapping element info, or null if no overlap
 */
export function findOverlappingElement(
  draggedId: string,
  draggedHead: number,
  draggedTail: number,
  positions: Map<string, ElementPosition>,
  durations: Map<string, number>,
): OverlapInfo | null {
  for (const [elementId, position] of positions) {
    // Skip the dragged element
    if (elementId === draggedId) {
      continue;
    }

    const duration = durations.get(elementId);
    if (duration === undefined) {
      continue;
    }

    const otherHead = position.globalTime;
    const otherTail = otherHead + duration;

    // Check for interval intersection (excluding exact adjacency)
    const isOverlapping = draggedHead < otherTail && draggedTail > otherHead;

    if (isOverlapping) {
      return {
        elementId,
        head: otherHead,
        tail: otherTail,
        midpoint: otherHead + duration / 2,
      };
    }
  }

  return null;
}

/**
 * Determines the resolved position for a dragged element that overlaps another.
 * Uses the midpoint rule to determine which side the dragged element lands on.
 *
 * @param dragDirection - 'left' or 'right' based on totalOffset sign
 * @param draggedHead - Current head position of dragged element
 * @param draggedTail - Current tail position of dragged element
 * @param draggedDuration - Duration of dragged element
 * @param overlapped - Info about the overlapped element
 * @returns Resolution result with snap boundary, resolved position, and swap flag
 */
export function resolveOverlapPosition(
  dragDirection: 'left' | 'right',
  draggedHead: number,
  draggedTail: number,
  draggedDuration: number,
  overlapped: OverlapInfo,
): OverlapResolutionResult {
  if (dragDirection === 'left') {
    // Leading edge is HEAD when dragging left
    if (draggedHead < overlapped.midpoint) {
      // Dragged element goes BEFORE overlapped → snap to overlapped's head
      return {
        snapBoundaryTime: overlapped.head,
        resolvedHead: overlapped.head,
        triggersSwap: true,
      };
    } else {
      // Dragged element goes AFTER overlapped → snap to overlapped's tail
      return {
        snapBoundaryTime: overlapped.tail,
        resolvedHead: overlapped.tail,
        triggersSwap: false,
      };
    }
  } else {
    // dragDirection === 'right'
    // Leading edge is TAIL when dragging right
    if (draggedTail > overlapped.midpoint) {
      // Dragged element goes AFTER overlapped → snap tail to overlapped's tail
      return {
        snapBoundaryTime: overlapped.tail,
        resolvedHead: overlapped.tail - draggedDuration,
        triggersSwap: true,
      };
    } else {
      // Dragged element goes BEFORE overlapped → snap tail to overlapped's head
      return {
        snapBoundaryTime: overlapped.head,
        resolvedHead: overlapped.head - draggedDuration,
        triggersSwap: false,
      };
    }
  }
}

/**
 * Extracts the boundaries of an overlapped element from regenerated boundaries.
 *
 * During overlap mode, we only want to consider snap boundaries from the
 * overlapped element itself (not playhead, markers, or other track elements).
 *
 * @param overlapped - Info about the overlapped element
 * @param regeneratedBoundaries - Regenerated main track boundaries
 * @returns Array containing only the overlapped element's head and tail boundaries
 */
export function getOverlappedElementBoundaries(
  overlapped: OverlapInfo,
  regeneratedBoundaries: ReadonlyArray<ElementBoundary>,
): ElementBoundary[] {
  return regeneratedBoundaries.filter(
    boundary => boundary.elementId === overlapped.elementId,
  );
}

/**
 * Options for applying declarative placements
 */
export interface ApplyPlacementsOptions {
  /** The element being dragged */
  draggedElement: ElementState;
  /** Total time offset from original position (NOT per-frame delta) */
  totalTimeOffset: number;
  /** Snap threshold in seconds */
  snapThreshold: number;
  /** All elements in the same track */
  trackElements: ReadonlyArray<ElementState>;
  /** TimelineStore for applying overrides */
  timelineStore: TimelineStore;
  /** VideoCreatorStore for snap guide feedback */
  videoCreatorStore: IVideoCreatorStore;
  /** Pre-calculated snap boundaries from drag start (all tracks) */
  snapshotBoundaries: ReadonlyArray<ElementBoundary>;
  /** Original globalTime of the dragged element (before drag started) */
  originalGlobalTime: number;
  /** Whether this is the final placement call (on drag release). When true, overlap resolution is applied. */
  isFinalPlacement?: boolean;
}

/**
 * Applies declarative placement computation for main video track elements.
 *
 * This function:
 * 1. Converts ElementState[] to TrackElement[] format
 * 2. Calls computeElementPlacements with total offset
 * 3. Applies results to TimelineStore overrides
 * 4. Sets swap flag for commit logic
 */
export function applyDeclarativePlacements(
  options: ApplyPlacementsOptions,
): void {
  const {
    draggedElement,
    totalTimeOffset,
    trackElements,
    timelineStore,
    videoCreatorStore,
    snapshotBoundaries,
    originalGlobalTime,
    isFinalPlacement = false,
  } = options;

  if (
    totalTimeOffset === 0 ||
    Math.abs(totalTimeOffset) < 1 / Math.min(timelineStore.framerate, 60)
  ) {
    return; // No movement, no need to compute placements
  }

  // Determine drag direction from totalOffset sign
  const dragDirection: 'left' | 'right' =
    totalTimeOffset < 0 ? 'left' : 'right';

  // Build a map of elementId -> track for filtering
  // Use EditorStore.tracksMap as single source of truth for track data
  const tracksMap = videoCreatorStore.rootStore.editorStore?.tracksMap;
  const elementTrackMap = new Map<string, number>();
  if (tracksMap) {
    for (const [trackNumber, elements] of tracksMap) {
      for (const el of elements) {
        elementTrackMap.set(el.id, trackNumber);
      }
    }
  }

  // Filter boundaries:
  // 1. Remove boundaries from hidden tracks (>= 32, including karaoke)
  // 2. Remove boundaries from the dragged element (should already be excluded, but be safe)
  const filteredBoundaries = snapshotBoundaries.filter(boundary => {
    // Keep reference boundaries (like playhead)
    if (boundary.type === 'reference') {
      return true;
    }

    // Filter out dragged element's boundaries
    if (boundary.elementId === draggedElement.source.id) {
      return false;
    }

    // Filter out hidden tracks
    const track = elementTrackMap.get(boundary.elementId);
    if (track !== undefined && track >= HIDDEN_TRACK_THRESHOLD) {
      return false;
    }

    return true;
  });

  // Convert to minimal format for pure computation
  const elements: TrackElement[] = trackElements.map(el => ({
    id: el.source.id,
    globalTime: el.globalTime,
    duration: el.duration,
  }));

  // Compute new placements declaratively (swap logic only)
  const result = computeElementPlacements(
    elements,
    draggedElement.source.id,
    totalTimeOffset,
  );

  // Build set of main track element IDs for boundary merging
  const mainTrackElementIds = new Set(elements.map(el => el.id));

  // Regenerate main track boundaries based on displaced positions
  const regeneratedMainTrack = regenerateMainTrackBoundaries(
    result,
    draggedElement.source.id,
    elements,
  );

  // Merge with other-track boundaries (filtered snapshot excluding main track)
  const mergedBoundaries = mergeTrackBoundaries(
    regeneratedMainTrack,
    filteredBoundaries,
    mainTrackElementIds,
  );

  // Build duration lookup map for overlap detection
  const durations = new Map<string, number>();
  for (const el of trackElements) {
    durations.set(el.source.id, el.duration);
  }

  // Get dragged element's proposed position
  let draggedPosition = result.positions.get(draggedElement.source.id);
  let snappingResult: EdgeSnappingResult = {
    snappedTime: 0,
    didSnap: false,
    isWithinWarningRange: false,
  };
  let finalResult = result;
  let overlapResolution: OverlapResolutionResult | null = null;
  let overlapped: OverlapInfo | null = null;

  if (draggedPosition) {
    const proposedHead = draggedPosition.proposedGlobalTime;
    const proposedTail = proposedHead + draggedElement.duration;

    // STEP 1: Check for overlap FIRST using proposed (unsnapped) position
    overlapped = findOverlappingElement(
      draggedElement.source.id,
      proposedHead,
      proposedTail,
      result.positions,
      durations,
    );

    // STEP 2: Apply edge snapping with appropriate restrictions
    if (overlapped) {
      // OVERLAP MODE: Only check leading edge against overlapped element's boundaries
      const leadingEdge: 'head' | 'tail' =
        dragDirection === 'left' ? 'head' : 'tail';
      const restrictedBoundaries = getOverlappedElementBoundaries(
        overlapped,
        regeneratedMainTrack,
      );

      snappingResult = applyEdgeSnapping(
        proposedHead,
        draggedElement.duration,
        restrictedBoundaries,
        options.snapThreshold,
        undefined, // TODO: Track currentSnap for stickiness
        leadingEdge,
      );
    } else {
      // NORMAL MODE: Check both edges against all boundaries
      snappingResult = applyEdgeSnapping(
        proposedHead,
        draggedElement.duration,
        mergedBoundaries,
        options.snapThreshold,
        undefined, // TODO: Track currentSnap for stickiness
        'both',
      );
    }

    // STEP 3: Handle snap results - check if snapped position triggers additional swap
    if (snappingResult.didSnap) {
      // Build first-pass displaced elements for swap check
      const firstPassElements: TrackElement[] = elements.map(el => {
        const pos = result.positions.get(el.id);
        return {
          id: el.id,
          globalTime: pos?.globalTime ?? el.globalTime,
          duration: el.duration,
        };
      });

      const triggersSwap = doesPositionTriggerSwap(
        firstPassElements,
        draggedElement.source.id,
        snappingResult.snappedTime,
        draggedElement.duration,
      );

      if (triggersSwap) {
        // Calculate the effective offset from the snapped position
        const snappedOffset = snappingResult.snappedTime - originalGlobalTime;

        // Recompute placements with the snapped offset
        finalResult = computeElementPlacements(
          elements,
          draggedElement.source.id,
          snappedOffset,
        );
      } else {
        // No additional swap - just update the dragged element's snapped position
        result.positions.set(draggedElement.source.id, {
          globalTime: snappingResult.snappedTime,
          proposedGlobalTime: draggedPosition.proposedGlobalTime,
        });
      }
    }

    // STEP 4: Overlap resolution (when overlapping but no snap occurred)
    // Re-check overlap with current positions (snap may have resolved it)
    if (!snappingResult.didSnap && overlapped) {
      const currentDraggedPos = finalResult.positions.get(
        draggedElement.source.id,
      );
      if (currentDraggedPos) {
        const draggedHead = currentDraggedPos.globalTime;
        const draggedTail = draggedHead + draggedElement.duration;

        // Calculate resolution based on midpoint rule
        overlapResolution = resolveOverlapPosition(
          dragDirection,
          draggedHead,
          draggedTail,
          draggedElement.duration,
          overlapped,
        );

        if (isFinalPlacement) {
          // Actually resolve: recompute with new offset to get consistent swap behavior
          const resolvedOffset =
            overlapResolution.resolvedHead - originalGlobalTime;
          finalResult = computeElementPlacements(
            elements,
            draggedElement.source.id,
            resolvedOffset,
          );
        }
        // If not final placement, positions stay overlapping (element follows cursor)
        // but snap guide will show resolved position below
      }
    }
  }

  // Apply final results to overrides
  for (const [elementId, position] of finalResult.positions) {
    const elementState = trackElements.find(el => el.source.id === elementId);
    if (elementState) {
      timelineStore.applyTimelinePlacementOverrideToElement(elementState, {
        globalTime: position.globalTime,
        proposedGlobalTime: position.proposedGlobalTime,
      });
    }
  }

  // Always set to true for declarative path so applyPlacements uses our
  // computed positions directly, bypassing legacy overlap detection logic
  timelineStore.setElementsWithPlacementOverridesHaveDifferentOffsets(true);

  // Update snap guide visual feedback
  // The snap guide shows WHERE we are snapping to (a boundary), not the element position.
  // Show guide when:
  // 1. Actually snapped to a boundary (didSnap = true)
  // 2. Within warning range (approaching a boundary, but not yet snapped)
  // 3. Overlapping an element (shows where element will land on release)
  if (snappingResult.didSnap && snappingResult.snappedBoundary) {
    // Actively snapped - show guide at snap boundary
    videoCreatorStore.setSnapGuide(true, snappingResult.snappedBoundary.time);
  } else if (overlapResolution) {
    // Overlapping - show guide at resolved position (where element will land)
    videoCreatorStore.setSnapGuide(true, overlapResolution.snapBoundaryTime);
  } else if (
    snappingResult.isWithinWarningRange &&
    snappingResult.warningBoundary
  ) {
    // Within warning range - show guide at approaching boundary
    videoCreatorStore.setSnapGuide(true, snappingResult.warningBoundary.time);
  } else {
    videoCreatorStore.setSnapGuide(false);
  }
}

/**
 * Gets track elements for the given element's track from EditorStore.
 *
 * @param tracksMap - The EditorStore tracksMap
 * @param trackNumber - The track number to get elements for
 * @returns Array of elements converted to ElementState-like format
 */
export function getTrackElementsFromEditor(
  tracksMap: Map<number, Element[]>,
  trackNumber: number,
): ElementState[] {
  const trackElements = tracksMap.get(trackNumber);
  if (!trackElements) return [];

  // Convert to ElementState-like format (same as useTimelineTracks does)
  return trackElements.map(el => ({
    source: el.getState(),
    duration: el.duration,
    globalTime: el.time,
    localTime: el.time,
    trimStart: el.trim_start,
    track: el.track,
    exitDuration: 0,
  })) as ElementState[];
}
